import type { ChannelModel } from "../types/radio-types";
import {
  isTelemetryStepAllowed,
  STEP_INDEX_0_5_KHZ
} from "./step-table";

export const TELEMETRY_MIN_FREQUENCY = 14_400_000;
export const TELEMETRY_MAX_FREQUENCY_EXCLUSIVE = 17_400_000;

export interface NormalizationResult {
  channels: ChannelModel[];
  adjustedCount: number;
}

function isChannelActiveTelemetry(band: number, rxFrequency: number): boolean {
  if (band < 0 || band > 6) {
    return false;
  }
  if (
    rxFrequency < TELEMETRY_MIN_FREQUENCY ||
    rxFrequency >= TELEMETRY_MAX_FREQUENCY_EXCLUSIVE
  ) {
    return false;
  }
  return true;
}

function sanitizeName(name: string): string {
  return name
    .trim()
    .replace(/[^\x20-\x7E]/g, "")
    .slice(0, 10);
}

export function normalizeChannels(input: ChannelModel[]): NormalizationResult {
  let adjustedCount = 0;

  const channels = input.map((channel) => {
    let changed = false;
    let name = sanitizeName(channel.name);
    let band = channel.band;
    let scanlistMask = channel.scanlistMask & 0xffff;
    let stepSetting = channel.stepSetting;
    let rxFrequency = channel.rxFrequency;
    let bandwidth: "wide" | "narrow" = channel.bandwidth;

    if (name !== channel.name) {
      changed = true;
    }

    if (channel.active && (!Number.isInteger(band) || band < 0 || band > 6)) {
      band = 3;
      changed = true;
    }

    if (scanlistMask !== channel.scanlistMask) {
      changed = true;
    }

    if (!isTelemetryStepAllowed(stepSetting)) {
      stepSetting = STEP_INDEX_0_5_KHZ;
      changed = true;
    }

    if (
      channel.active &&
      (rxFrequency < TELEMETRY_MIN_FREQUENCY ||
        rxFrequency >= TELEMETRY_MAX_FREQUENCY_EXCLUSIVE)
    ) {
      rxFrequency = TELEMETRY_MIN_FREQUENCY;
      changed = true;
    }

    if (bandwidth !== "narrow") {
      bandwidth = "narrow";
      changed = true;
    }

    const active = channel.active ? isChannelActiveTelemetry(band, rxFrequency) : false;
    if (active !== channel.active) {
      changed = true;
    }

    if (!changed) {
      return channel;
    }

    adjustedCount += 1;
    return {
      ...channel,
      name,
      active,
      band,
      scanlistMask,
      stepSetting,
      rxFrequency,
      bandwidth
    };
  });

  return { channels, adjustedCount };
}

export const sanitizeChannels = normalizeChannels;
